import { HqMetaRowHint, OnlyHqNotice } from "../components/HqMetaHint";
import { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle,
  Plus,
  Trash2,
  Pencil,
  X,
  Send,
  Check,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import type { ProjectDetail } from "../lib/project-detail";
import { useProjectDetail } from "../lib/project-detail";
import { ApiError } from "../lib/api";
import { Combobox } from "../components/Combobox";
import { ConsentGapAdvice, ConsentModeOptions } from "../components/ConsentModeOptions";
import { RuleMetaHqOnly } from "../components/RuleMetaHqOnly";
import { RuleMetaLine, RulePixelBox } from "../components/RuleMetaStatus";
import { RulePixelConfirm } from "../components/RulePixelConfirm";
import { SettingRow, SettingsPanel } from "../components/SettingRow";
import {
  useTrackingConfig,
  useUpdateTrackingConfig,
  useDestinations,
  useCreateDestination,
  useUpdateDestination,
  useDeleteDestination,
  useTestDestination,
  useDestinationEmq,
  useCplSuggestion,
  useGoogleAdsWebConversions,
  withFreshServerConfig,
  useGoogleAdsConversionActions,
  useGoogleAdsAccounts,
  type GoogleAdsAccountAction,
  type EmqResult,
  type CplSuggestionResult,
  useTrackingAuditLog,
  usePixelDelivery,
  useMetaPixels,
  useMetaPixelCapiCheck,
  useCookieScan,
  useTriggerCookieScan,
  useDismissFinding,
  useRestoreFinding,
  metaEventName,
  googleToggleOffHint,
  trackingSaveErrorText,
  type DeniedStage,
  type ConsentCategory,
  type DestinationType,
  type FingerprintMode,
  type EventRule,
  type TrackingConfigInput,
  type TrackingDestination,
  type TrackingDestinationInput,
} from "../lib/project-tracking";
import { useEventTypes } from "../lib/customers";
import { useWebhookEndpoints } from "../lib/webhook-endpoints";
import { buildMetaRoutingRows } from "../lib/meta-routing-rows";
import { WebConversionButton, WebConversionNotice } from "../components/WebConversionNotice";

interface TabContext {
  project: ProjectDetail | undefined;
  isLoading: boolean;
  projectId: number;
}
function useTabContext(): TabContext {
  return useOutletContext<TabContext>();
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div>
        <h2 className="font-mono text-xs uppercase tracking-wider text-text-tertiary">
          {title}
        </h2>
        {description && (
          <p className="mt-1 text-sm text-text-secondary">{description}</p>
        )}
      </div>
      {children}
    </section>
  );
}

/** Small reusable collapsible disclosure for "advanced" content. */
function Disclosure({ title, children }: { title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-border-primary bg-bg-secondary">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left"
      >
        {open ? (
          <ChevronDown size={15} className="text-text-tertiary" />
        ) : (
          <ChevronRight size={15} className="text-text-tertiary" />
        )}
        <span className="text-sm font-medium text-text-primary">{title}</span>
      </button>
      {open && (
        <div className="space-y-6 border-t border-border-primary px-4 py-4">{children}</div>
      )}
    </div>
  );
}

/** Copy-paste link for the customer's privacy policy — reopens the cookie
 *  banner via vanilla-cookieconsent's built-in data attribute. */
export const CONSENT_REOPEN_SNIPPET =
  '<a href="#" data-cc="show-preferencesModal">Cookie-Einstellungen ändern</a>';

/** Developer snippet (Ebene B) — manual conversion firing from page code. */
const DHC_TRACK_SNIPPET =
  "dhcTrack.track('Lead', { value: 99, currency: 'EUR', email: '...' });";

/** Customer-facing instruction to generate a Meta Conversions-API access token,
 *  with the pixel's Events-Manager settings deep-link prefilled. */
function buildTokenInstruction(pixelId: string, pixelName: string): string {
  return [
    `Hallo,`,
    ``,
    `damit wir Conversions zuverlässig und werbeblocker-resistent serverseitig an Facebook senden können, brauchen wir einen Conversions-API-Zugriffsschlüssel für euer Pixel „${pixelName || pixelId}". So bekommt ihr ihn:`,
    ``,
    `1. Öffnet die Pixel-Einstellungen direkt hier:`,
    `   https://business.facebook.com/events_manager2/list/dataset/${pixelId}/settings`,
    `2. Scrollt zum Abschnitt „Conversions API" und klickt auf „Zugriffsschlüssel generieren" (Generate access token).`,
    `3. Kopiert den angezeigten Schlüssel und schickt ihn uns zurück.`,
    ``,
    `Der Schlüssel dient ausschließlich dem sicheren Server-Versand eurer eigenen Events. Wir behandeln ihn vertraulich.`,
    ``,
    `Danke!`,
  ].join("\n");
}

const STAGE_LABEL: Record<DeniedStage, string> = {
  A: "A — Sendet die Daten IMMER an die Plattform, auch wenn jemand der Cookie-Nutzung widerspricht. Rechtlich riskant — nur mit Freigabe von Anwalt/Datenschutzbeauftragtem.",
  B: "B — Bei Widerspruch melden wir das Ereignis trotzdem direkt von unserem Server an die Plattform. Rechtlich umstritten — nur mit juristischer Freigabe.",
  C: "C — Sendet nur, wenn die Person zugestimmt hat. Ohne Zustimmung wird nichts gemeldet. Empfohlen.",
};

const STAGE_BADGE: Record<DeniedStage, string> = {
  A: "bg-danger-bg text-danger",
  B: "bg-warning-bg text-warning",
  C: "bg-success-bg text-success",
};

/** Modal shown before escalating a denied-stage to A or B. */
function RiskAckDialog({
  stage,
  onConfirm,
  onCancel,
}: {
  stage: DeniedStage;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg space-y-4 rounded-lg border border-danger bg-bg-primary p-6 shadow-xl">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 shrink-0 text-danger" size={20} />
          <div className="space-y-2">
            <h3 className="text-base font-semibold text-text-primary">
              Stufe {stage} bestätigen — rechtliches Risiko
            </h3>
            <p className="text-sm text-text-secondary">
              {stage === "A"
                ? "Stufe A meldet Conversions auch OHNE Zustimmung an die Plattform — im Browser und direkt von unserem Server, mit allen persönlichen Merkmalen. Nur vertretbar, wenn der Kunde die rechtliche Grundlage über sein eigenes Cookie-Banner sicherstellt."
                : "Stufe B meldet Conversions auch OHNE Zustimmung direkt von unserem Server. Rechtlich stark umstritten — nur mit dokumentierter Abwägung und juristischer Freigabe nutzen."}
            </p>
            <p className="text-sm text-text-secondary">
              Diese Entscheidung wird mit deinem Namen und Zeitpunkt protokolliert.
              Keine Rechtsberatung — im Zweifel mit Anwalt/Datenschutzbeauftragtem prüfen.
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-border-primary bg-bg-primary px-3 py-1.5 text-sm text-text-secondary hover:bg-bg-hover"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-md border border-danger bg-danger-bg px-3 py-1.5 text-sm font-medium text-danger hover:opacity-90"
          >
            Risiko verstanden, Stufe {stage} setzen
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls =
  "w-full rounded-md border border-border-primary bg-bg-secondary px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none";

// ---------------------------------------------------------------------------
// Cookie scan (headless) — trigger + show detected cookies/trackers per category.
// ---------------------------------------------------------------------------

const CAT_LABEL: Record<string, string> = {
  necessary: "Notwendig",
  functional: "Funktional",
  statistics: "Statistik",
  marketing: "Marketing",
};

function CookieScanSection({ projectId }: { projectId: number }) {
  const scan = useCookieScan(projectId);
  const trigger = useTriggerCookieScan(projectId);
  const dismiss = useDismissFinding(projectId);
  const restore = useRestoreFinding(projectId);
  const [url, setUrl] = useState("");
  const data = scan.data?.scan;
  const dismissed = scan.data?.dismissed ?? [];

  const groups: Record<string, { kind: "cookie" | "tracker"; id: string; provider: string; description: string }[]> = {};
  for (const f of data?.findings ?? []) {
    (groups[f.category] = groups[f.category] ?? []).push(f);
  }

  const runScan = (override?: string) =>
    trigger.mutate(override, {
      onSuccess: () => toast.success("Scan gestartet — Ergebnis erscheint in ~30 s"),
      onError: (e) => {
        const code = ((e as ApiError)?.body as { error?: string } | undefined)?.error ?? "";
        const known: Record<string, string> = {
          no_allowed_domains: "Keine erlaubten Domains hinterlegt — bitte zuerst im Tab „Setup“ pflegen.",
          url_not_allowed:
            "Diese Adresse gehört nicht zu den erlaubten Domains des Projekts — bitte eine eigene Seite angeben oder die Domain im Tab „Setup“ ergänzen.",
        };
        toast.error(known[code] ?? `Scan fehlgeschlagen: ${(e as Error).message}`);
      },
    });

  return (
    <Section
      title="Cookie-Scan (Beta)"
      description="Ruft deine Seiten mit einem echten Browser auf (ohne zuzustimmen) und zeigt, welche Cookies und Tracker dort tatsächlich gesetzt werden — sortiert nach Art. Standardmäßig werden deine eingetragenen Webseiten geprüft (auch automatisch jede Woche); auf Wunsch auch eine einzelne Adresse."
    >
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={trigger.isPending}
          onClick={() => runScan(undefined)}
          className="rounded-md border border-accent bg-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          Erlaubte Domains scannen
        </button>
        <span className="text-xs text-text-tertiary">oder einzelne URL:</span>
        <input
          className={inputCls + " flex-1 min-w-[200px]"}
          placeholder="https://kunde.de/funnel"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        <button
          type="button"
          disabled={trigger.isPending || !/^https?:\/\/.+/i.test(url)}
          onClick={() => runScan(url)}
          className="rounded-md border border-border-primary bg-bg-secondary px-3 py-2 text-sm font-medium text-text-primary hover:bg-bg-hover disabled:opacity-50"
        >
          URL scannen
        </button>
      </div>

      {data && (
        <div className="mt-3 text-sm">
          {data.status === "pending" && <p className="text-text-tertiary">Scan läuft… ({data.scanned_url})</p>}
          {data.status === "failed" && <p className="text-danger">Scan fehlgeschlagen: {data.error}</p>}
          {data.status === "done" && (
            <div className="space-y-2">
              <p className="text-xs text-text-tertiary">
                Zuletzt gescannt: {data.scanned_url}
                {data.scanned_at ? ` · ${new Date(data.scanned_at).toLocaleString()}` : ""} · {data.findings.length} Treffer
              </p>
              {(["necessary", "functional", "statistics", "marketing"] as const).map((cat) =>
                groups[cat] && groups[cat]!.length > 0 ? (
                  <div key={cat} className="rounded-md border border-border-primary bg-bg-secondary px-3 py-2">
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-text-tertiary">{CAT_LABEL[cat]}</p>
                    <ul className="space-y-1 text-xs text-text-secondary">
                      {groups[cat]!.map((f, i) => (
                        <li key={i} className="flex items-start justify-between gap-2">
                          <span className="flex flex-wrap gap-x-2">
                            <span className="font-mono text-text-primary">{f.id}</span>
                            <span className="text-text-tertiary">· {f.provider}</span>
                            <span>· {f.description}</span>
                          </span>
                          <button
                            type="button"
                            title="Verwerfen — taucht beim nächsten Scan nicht wieder auf"
                            disabled={dismiss.isPending}
                            onClick={() => dismiss.mutate({ kind: f.kind, id: f.id })}
                            className="shrink-0 text-text-tertiary hover:text-danger disabled:opacity-50"
                          >
                            <X size={13} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null,
              )}
              {data.findings.length === 0 && <p className="text-text-tertiary">Keine bekannten Tracker/Cookies erkannt.</p>}
            </div>
          )}
        </div>
      )}

      {dismissed.length > 0 && (
        <div className="mt-3 rounded-md border border-dashed border-border-primary px-3 py-2">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-text-tertiary">
            Ausgeblendet ({dismissed.length})
          </p>
          <ul className="space-y-1 text-xs text-text-tertiary">
            {dismissed.map((d) => (
              <li key={`${d.kind}:${d.id}`} className="flex items-center justify-between gap-2">
                <span className="flex gap-x-2">
                  <span className="font-mono text-text-secondary line-through">{d.id}</span>
                  <span>· {d.kind === "cookie" ? "Cookie" : "Tracker"}</span>
                </span>
                <button
                  type="button"
                  disabled={restore.isPending}
                  onClick={() => restore.mutate({ kind: d.kind, id: d.id })}
                  className="shrink-0 text-accent hover:underline disabled:opacity-50"
                >
                  Wiederherstellen
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Consent tab
// ---------------------------------------------------------------------------

export function ProjectConsentTab() {
  const { projectId } = useTabContext();
  const { data, isLoading } = useTrackingConfig(projectId);
  const update = useUpdateTrackingConfig(projectId);
  const audit = useTrackingAuditLog(projectId);

  const [form, setForm] = useState<TrackingConfigInput>({});
  const [pendingAck, setPendingAck] = useState<DeniedStage | null>(null);

  useEffect(() => {
    if (data?.config) {
      const c = data.config;
      setForm({
        consent_mode: c.consent_mode,
        consent_mode_v2: c.consent_mode_v2,
        default_denied_stage: c.default_denied_stage,
        categories: c.categories,
        bypass_rules: c.bypass_rules,
        banner_config: c.banner_config,
        legal_text_ref: c.legal_text_ref,
        cookie_autoblock_enabled: c.cookie_autoblock_enabled,
      });
    }
  }, [data?.config]);

  if (isLoading || !data) return <p className="text-sm text-text-secondary">Lade…</p>;

  const original = data.config;
  const set = <K extends keyof TrackingConfigInput>(k: K, v: TrackingConfigInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const toggleCategory = (cat: ConsentCategory) => {
    const cur = form.categories ?? original.categories;
    const next = cur.includes(cat) ? cur.filter((x) => x !== cat) : [...cur, cat];
    if (!next.includes("necessary")) next.unshift("necessary");
    set("categories", next);
  };

  const save = (ack = false) => {
    const escalating =
      (form.default_denied_stage === "A" || form.default_denied_stage === "B") &&
      form.default_denied_stage !== original.default_denied_stage;
    if (escalating && !ack) {
      setPendingAck(form.default_denied_stage!);
      return;
    }
    update.mutate(
      { ...form, risk_acknowledged: ack },
      {
        onSuccess: () => toast.success("Consent-Einstellungen gespeichert"),
        onError: (e) => toast.error(`Speichern fehlgeschlagen: ${(e as Error).message}`),
      },
    );
    setPendingAck(null);
  };

  const mode = form.consent_mode ?? original.consent_mode;
  const deniedStage = form.default_denied_stage ?? original.default_denied_stage;
  const modeLabel = mode === "banner" ? "DHC-Banner" : mode === "off" ? "Kein Banner" : "Externes Banner";

  return (
    <div className="space-y-6 pb-12">
      <div className="flex items-start gap-2 rounded-md border border-border-primary bg-bg-secondary px-4 py-3 text-sm text-text-secondary">
        <AlertTriangle size={16} className="mt-0.5 shrink-0 text-text-tertiary" />
        <span>
          <strong className="text-text-primary">Hinweis (keine Rechtsberatung):</strong> Die
          Vorlagen für Rechtstexte ersetzen keinen Anwalt; verantwortlich bleibt der Kunde.
          Standard bei Widerspruch ist Stufe C (DSGVO/TDDDG-konform).
        </span>
      </div>

      {/* Consent-Status hero — read-only summary of current selections. */}
      <div className="grid gap-3 rounded-lg border border-border-primary bg-bg-secondary p-4 sm:grid-cols-2">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-wider text-text-tertiary">
            Einwilligung
          </p>
          <p className="mt-1 text-base font-semibold text-text-primary">{modeLabel}</p>
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-wider text-text-tertiary">
            Bei Ablehnung
          </p>
          <p className="mt-1">
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-sm font-semibold ${STAGE_BADGE[deniedStage]}`}
            >
              Stufe {deniedStage}
            </span>
          </p>
        </div>
      </div>

      <SettingsPanel>
      <SettingRow
        title="Wie wird eingewilligt?"
        description="Wie holst du auf deinen Seiten die Zustimmung zu Cookies ein?"
        wide
      >
        <ConsentModeOptions mode={mode} onChange={(m) => set("consent_mode", m)} />
        {mode === "off" && (
          <p className="mt-3 flex items-center gap-2 text-xs text-warning">
            <AlertTriangle size={14} /> Ohne Banner — der Kunde muss die nötige Erlaubnis
            selbst sicherstellen.
          </p>
        )}
      </SettingRow>

      <SettingRow
        title="Bei Ablehnung"
        description="Was passiert, wenn jemand der Cookie-Nutzung widerspricht. Gilt für alle Werbe-Plattformen, lässt sich pro Ziel aber anpassen."
        wide
      >
        <div className="grid gap-3 sm:grid-cols-3">
          {(["C", "B", "A"] as DeniedStage[]).map((s) => {
            const active = deniedStage === s;
            return (
              <button
                key={s}
                type="button"
                onClick={() => set("default_denied_stage", s)}
                className={`rounded-lg border p-3 text-left transition-colors ${
                  active
                    ? "border-accent bg-bg-secondary"
                    : "border-border-primary bg-bg-secondary hover:bg-bg-hover"
                }`}
              >
                <span
                  className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-sm font-bold ${STAGE_BADGE[s]}`}
                >
                  {s}
                </span>
                <span className="mt-2 block text-xs text-text-secondary">{STAGE_LABEL[s]}</span>
              </button>
            );
          })}
        </div>
      </SettingRow>

      <SettingRow title="Cookie-Kategorien">
        <div className="flex flex-wrap gap-2">
          {(["necessary", "functional", "statistics", "marketing"] as ConsentCategory[]).map((cat) => {
            const checked = (form.categories ?? original.categories).includes(cat);
            const locked = cat === "necessary";
            const label =
              cat === "necessary"
                ? "Notwendig (immer an)"
                : cat === "functional"
                  ? "Funktional"
                  : cat === "statistics"
                    ? "Statistik"
                    : "Marketing";
            return (
              <label
                key={cat}
                className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors ${
                  locked ? "cursor-default" : "cursor-pointer"
                } ${
                  checked
                    ? "border-accent bg-accent/10 text-text-primary"
                    : "border-border-primary text-text-secondary hover:bg-bg-hover"
                }`}
              >
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={checked}
                  disabled={locked}
                  onChange={() => toggleCategory(cat)}
                />
                {checked ? (
                  <Check size={13} className="text-accent" />
                ) : (
                  <span className="h-[13px] w-[13px] rounded-full border border-border-primary" />
                )}
                {label}
              </label>
            );
          })}
        </div>
      </SettingRow>

      <SettingRow
        title="Cookie-Blocking & Scan"
        description="Bekannte fremde Tracker (z. B. von Facebook, Google) werden automatisch blockiert, bis jemand zustimmt. Außerdem kannst du prüfen lassen, welche Cookies auf deinen Seiten wirklich gesetzt werden."
        wide
      >
        <div className="space-y-6">
        <Section
          title="Cookie-Auto-Block (Beta)"
          description="Blockt bekannte fremde Tracker (Facebook-Pixel, Google Analytics, LinkedIn, TikTok …) automatisch, bis ein Besucher zustimmt — so ähnlich wie Cookiebot. Standardmäßig aus."
        >
          <label className="flex items-center gap-2 text-sm text-text-primary">
            <input
              type="checkbox"
              checked={form.cookie_autoblock_enabled ?? original.cookie_autoblock_enabled}
              onChange={(e) => set("cookie_autoblock_enabled", e.target.checked)}
            />
            Auto-Block für dieses Projekt aktivieren
          </label>
          <p className="mt-2 text-xs text-text-tertiary">
            Funktioniert bei den meisten Trackern. Teste es vor dem Einschalten an einer echten Seite — und stelle sicher, dass der aktuelle Tracking-Code eingebaut ist (das <code>b.js</code> muss enthalten sein).
          </p>
        </Section>

        <CookieScanSection projectId={projectId} />
        </div>
      </SettingRow>

      <SettingRow title="Banner-Design" description="Aussehen und Sprache deines Cookie-Banners.">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs text-text-tertiary">Sprache</label>
            <select
              className={inputCls}
              value={form.banner_config?.default_lang ?? original.banner_config.default_lang ?? "de"}
              onChange={(e) =>
                set("banner_config", { ...form.banner_config, default_lang: e.target.value as "de" | "en" })
              }
            >
              <option value="de">Deutsch</option>
              <option value="en">English</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-text-tertiary">Position</label>
            <select
              className={inputCls}
              value={form.banner_config?.position ?? original.banner_config.position ?? "cloud"}
              onChange={(e) =>
                set("banner_config", {
                  ...form.banner_config,
                  position: e.target.value as "bottom" | "top" | "center" | "cloud",
                })
              }
            >
              <option value="cloud">Mittig unten, abgedunkelt (empfohlen)</option>
              <option value="bottom">Unten</option>
              <option value="top">Oben</option>
              <option value="center">Mitte</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-text-tertiary">Primärfarbe (hex)</label>
            <input
              className={inputCls}
              placeholder="#1a1a1a"
              value={form.banner_config?.primary_color ?? original.banner_config.primary_color ?? ""}
              onChange={(e) =>
                set("banner_config", { ...form.banner_config, primary_color: e.target.value })
              }
            />
          </div>
        </div>
      </SettingRow>
      </SettingsPanel>

      <Disclosure title="Erweitert">
        <SettingsPanel>
        <SettingRow
          title="Bypass-URLs"
          description="Seiten, auf denen das Tracking IMMER lädt — egal, was sonst eingestellt ist. Praktisch für Danke-Seiten, auf denen eine Conversion sicher gezählt werden muss. Eine Adresse (oder Teil davon) pro Zeile."
          wide
        >
          <textarea
            className={`${inputCls} h-24 w-full font-mono`}
            placeholder="/danke&#10;/webinar-bestaetigung"
            value={(form.bypass_rules?.urls ?? original.bypass_rules.urls).join("\n")}
            onChange={(e) =>
              set("bypass_rules", {
                urls: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean),
                codes: form.bypass_rules?.codes ?? original.bypass_rules.codes,
              })
            }
          />
        </SettingRow>

        <SettingRow
          title="Rechtstext-Vorlage"
          description="Welche Vorlage für die Rechtstexte verwendet wird."
        >
          <input
            className={`${inputCls} sm:max-w-xs`}
            placeholder="z.B. de_v1"
            value={form.legal_text_ref ?? ""}
            onChange={(e) => set("legal_text_ref", e.target.value || null)}
          />
        </SettingRow>

        <SettingRow
          title="Link für die Datenschutzerklärung"
          description="Diesen Link in die Datenschutzerklärung einbauen. Ein Klick öffnet jederzeit die Cookie-Einstellungen — zusätzlich zum Schloss-Symbol unten rechts auf der Seite."
          wide
        >
          <div className="flex items-start gap-2">
            <code className="flex-1 overflow-x-auto whitespace-pre rounded-md border border-border-primary bg-bg-tertiary px-3 py-2 font-mono text-xs text-text-primary">
              {CONSENT_REOPEN_SNIPPET}
            </code>
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(CONSENT_REOPEN_SNIPPET);
                toast.success("Snippet kopiert");
              }}
              className="rounded-md border border-border-primary px-3 py-2 text-xs text-text-secondary hover:bg-bg-hover"
            >
              Kopieren
            </button>
          </div>
        </SettingRow>

        {audit.data && audit.data.entries.length > 0 && (
          <SettingRow title="Audit-Log" description="Die letzten wichtigen Änderungen an diesen Einstellungen — wer was wann geändert hat." wide>
            <ul className="space-y-1 text-xs text-text-secondary">
              {audit.data.entries.slice(0, 8).map((e) => (
                <li key={e.id} className="flex flex-wrap gap-x-2">
                  <span className="text-text-tertiary">{new Date(e.created_at).toLocaleString()}</span>
                  <span className="font-mono">{e.action}</span>
                  {e.from_value && <span>{e.from_value} → {e.to_value}</span>}
                  <span className="text-text-tertiary">{e.actor_email}</span>
                  {e.risk_acknowledged && <span className="text-danger">[Risiko bestätigt]</span>}
                </li>
              ))}
            </ul>
          </SettingRow>
        )}
        </SettingsPanel>
      </Disclosure>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => save(false)}
          disabled={update.isPending}
          className="rounded-md border border-accent bg-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          {update.isPending ? "Speichere…" : "Speichern"}
        </button>
        <span className="text-xs text-text-tertiary">Policy-Version {original.policy_version}</span>
      </div>

      {pendingAck && (
        <RiskAckDialog
          stage={pendingAck}
          onConfirm={() => save(true)}
          onCancel={() => setPendingAck(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Destinations tab
// ---------------------------------------------------------------------------

const TYPE_LABEL: Record<DestinationType, string> = {
  dhctrack: "DHC (First-Party)",
  meta: "Meta",
  google: "Google (GA4)",
  google_ads: "Google Ads (Offline)",
  linkedin: "LinkedIn",
  custom: "Custom",
};

const emptyForm: TrackingDestinationInput = {
  type: "meta",
  category: "marketing",
  denied_stage: "C",
  active: true,
  bypass_rules: { urls: [], codes: [] },
};

/**
 * Tracking mechanics (moved out of the Consent tab): event→Meta routing and
 * browser-conversion. Exposed as a hook so the Destinations tab can place the
 * Meta-routing table in the main settings panel while the browser-event pieces
 * live in an "advanced" disclosure below — all driven by ONE shared form +
 * save (partial saves merge server-side, so it never clobbers consent settings).
 */
function useTrackingMechanics(projectId: number): {
  eventsTable: React.ReactNode;
  browserEvents: React.ReactNode;
  browserConversion: React.ReactNode;
  mergeBuffer: React.ReactNode;
  save: () => void;
  saving: boolean;
} {
  const { data, isLoading } = useTrackingConfig(projectId);
  const update = useUpdateTrackingConfig(projectId);
  const eventTypes = useEventTypes(projectId);
  const webhookEndpoints = useWebhookEndpoints(projectId);
  const destinationsQuery = useDestinations(projectId);
  const cplSuggestion = useCplSuggestion(projectId);
  const [cplResults, setCplResults] = useState<Record<number, CplSuggestionResult>>({});
  const [cplLoadingId, setCplLoadingId] = useState<number | null>(null);

  const [form, setForm] = useState<TrackingConfigInput>({});
  useEffect(() => {
    if (data?.config) {
      const c = data.config;
      setForm({
        browser_conversion_event: c.browser_conversion_event,
        meta_event_allowlist: c.meta_event_allowlist,
        meta_event_values: c.meta_event_values,
        event_rules: c.event_rules,
        capi_merge_buffer_seconds: c.capi_merge_buffer_seconds,
        new_customer_event_types: c.new_customer_event_types,
      });
    }
  }, [data?.config]);

  if (isLoading || !data) {
    const loading = <p className="text-sm text-text-secondary">Lade…</p>;
    return {
      eventsTable: loading,
      browserEvents: loading,
      browserConversion: loading,
      mergeBuffer: loading,
      save: () => {},
      saving: false,
    };
  }

  const original = data.config;
  const set = <K extends keyof TrackingConfigInput>(k: K, v: TrackingConfigInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  // Routing rows: every event type we've received, plus (TASK-114) the
  // default event type of every connected, non-disabled webhook endpoint —
  // so an operator can enable Meta routing before the first event arrives.
  // PageView is always sent to Meta server-side and can't be unchecked;
  // every other type is off by default. The allowlist holds exactly the
  // non-PageView types the operator enabled (null/empty = none → "PageView
  // only").
  const routingRows = buildMetaRoutingRows(
    eventTypes.data?.items ?? [],
    webhookEndpoints.data?.items ?? [],
  );
  // LeadNewCustomer is a synthetic routing label — the new-customer routing
  // (TASK-13) retypes a new contact's CAPI job to it. It's never a *received*
  // event type, so it never shows up in eventTypes; surface it as an extra
  // selectable row.
  //
  // Ticking it is OPTIONAL since TASK-33: the gate also accepts the job's
  // origin_event_type (capi-dispatch.ts), so enabling the source type — Lead —
  // already covers the new-customer send it becomes. The row stays because an
  // already-ticked LeadNewCustomer keeps working and some operators prefer
  // seeing the routing target explicitly.
  const NEW_CUSTOMER_TYPE = "LeadNewCustomer";
  const displayItems = routingRows.some((i) => i.eventType === NEW_CUSTOMER_TYPE)
    ? routingRows
    : [...routingRows, { eventType: NEW_CUSTOMER_TYPE, count: 0, connected: false }];
  // The new-customer routing only runs on the Postgres identity engine; warn so
  // an operator on a not-yet-cut-over legacy (bigquery/dual) project isn't
  // misled into thinking the toggle is active.
  const projectDetail = useProjectDetail(projectId);
  const nonPostgresEngine =
    projectDetail.data != null && projectDetail.data.identityEngine !== "postgres";
  const stored = form.meta_event_allowlist ?? null;
  const selected = stored ?? [];
  const toggleMeta = (t: string) => {
    if (t === "PageView") return;
    const base = stored ?? [];
    set(
      "meta_event_allowlist",
      base.includes(t) ? base.filter((x) => x !== t) : [...base, t],
    );
  };
  // The routing table above is the sole CAPI authority: a URL rule's event only
  // reaches Meta when its event type is enabled there (PageView is always on).
  const goesToMeta = (eventType: string) =>
    eventType === "PageView" || selected.includes(eventType);

  // Neukunden-Modus (TASK-9): config-only checklist, no runtime routing yet
  // (see TASK-13). Same toggle mechanic as the Meta allowlist above, but no
  // event type is special-cased — "off" is simply an empty/null selection.
  const selectedNewCustomer = form.new_customer_event_types ?? [];
  const toggleNewCustomer = (t: string) => {
    const base = form.new_customer_event_types ?? [];
    set(
      "new_customer_event_types",
      base.includes(t) ? base.filter((x) => x !== t) : [...base, t],
    );
  };

  // Default-Wert pro Event-Typ (L7): fliesst als value/currency in Browser-
  // Pixel UND Server-CAPI, wenn das Event keinen eigenen deal_value hat.
  const eventValues = form.meta_event_values ?? {};
  const setEventValue = (t: string, raw: string) => {
    const next = { ...eventValues };
    const num = parseFloat(raw.replace(",", "."));
    if (raw.trim() === "" || !Number.isFinite(num) || num < 0) delete next[t];
    else next[t] = { value: num, currency: "EUR" };
    set("meta_event_values", Object.keys(next).length ? next : null);
  };

  // Lead-Klasse-Event-Typen — teilen sich den 10€-Fallback und sind das Ziel
  // eines übernommenen CPL-Vorschlags (TASK-24).
  const LEAD_VALUE_TYPES = ["Lead", "HQLead", "CompleteRegistration", "HQLeadRegistration", "LeadNewCustomer"];

  // CPL-Vorschlag (TASK-24 / #152, testweise): on-demand Meta-Insights-Abruf
  // je Meta-Ziel; "übernehmen" befüllt nur LEER stehende Lead-Wert-Felder —
  // manuell gepflegte Werte werden nie überschrieben.
  const fetchCplSuggestion = (destId: number) => {
    setCplLoadingId(destId);
    cplSuggestion.mutate(destId, {
      onSuccess: (r) => setCplResults((m) => ({ ...m, [destId]: r })),
      onSettled: () => setCplLoadingId(null),
    });
  };
  const applyCplSuggestion = (suggestion: number) => {
    const next = { ...eventValues };
    let changed = false;
    for (const t of LEAD_VALUE_TYPES) {
      if (next[t] == null) {
        next[t] = { value: suggestion, currency: "EUR" };
        changed = true;
      }
    }
    if (changed) set("meta_event_values", next);
  };
  const metaDestinations = (destinationsQuery.data?.destinations ?? []).filter(
    (d) => d.type === "meta" && d.active,
  );
  // TASK-83: the per-rule Google toggles (and their hint line) only make
  // sense — and only appear — when the project actually has an active
  // connection to that Google target (AC6).
  const googleAdsDest = (destinationsQuery.data?.destinations ?? []).find(
    (d) => d.type === "google_ads" && d.active,
  );
  const ga4Dest = (destinationsQuery.data?.destinations ?? []).find(
    (d) => d.type === "google" && d.active,
  );
  const gadsConversionActions =
    (googleAdsDest?.extra_config?.["conversion_actions"] as Record<string, string> | undefined) ?? {};
  const eur = (n: number) => n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const cplErrorHint = (r: Extract<CplSuggestionResult, { ok: false }>): string => {
    if (r.error === "no_ad_account") return r.message ?? "Kein Werbekonto im DH-Portal zugeordnet.";
    if (r.error === "not_configured") return "Meta-System-User-Token nicht konfiguriert.";
    if (r.error === "no_leads") return "Keine Leads im Zeitraum — kein Vorschlag möglich.";
    return `Meta-Fehler: ${r.message ?? "unbekannt"}`;
  };

  // URL-rules (Ebene A): no-code "URL → Event" rows.
  const rules = form.event_rules ?? original.event_rules ?? [];
  const setRules = (next: EventRule[]) => set("event_rules", next);
  const addRule = () =>
    setRules([...rules, { match: "contains", url: "", event_type: "Lead", browser: true, server: true }]);
  const updateRule = (i: number, patch: Partial<EventRule>) =>
    setRules(rules.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeRule = (i: number) => setRules(rules.filter((_, idx) => idx !== i));

  const [saveError, setSaveError] = useState<string | null>(null);
  const save = () => {
    setSaveError(null);
    update.mutate(form, {
      onSuccess: () => toast.success("Tracking-Einstellungen gespeichert"),
      onError: (e) => {
        const text = trackingSaveErrorText(e);
        const code = e instanceof ApiError ? (e.body as { error?: string } | undefined)?.error : undefined;
        setSaveError(code === "pixel_confirm_invalid" ? text : null);
        toast.error(`Speichern fehlgeschlagen: ${text}`);
      },
    });
  };

  const eventsTable =
    routingRows.length === 0 ? (
      <p className="text-xs text-text-tertiary">Noch keine Events empfangen.</p>
    ) : (
      <div className="overflow-x-auto">
        {nonPostgresEngine && (
          <p className="mb-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning">
            Dieses Projekt läuft noch auf der Legacy-Identity-Engine
            (BigQuery/Dual). Der Neukunden-Modus (Spalte „Neukunden") wirkt erst
            nach dem Postgres-Cutover — bis dahin bleibt er wirkungslos.
          </p>
        )}
        {metaDestinations.length > 0 && (
          <div className="mb-2 space-y-1.5">
            {metaDestinations.map((d) => {
              const r = cplResults[d.id];
              return (
                <div key={d.id} className="flex flex-wrap items-center gap-2 text-xs">
                  <button
                    type="button"
                    className="rounded-md border border-border-primary px-2 py-1 text-text-primary hover:bg-bg-tertiary"
                    disabled={cplLoadingId === d.id}
                    onClick={() => fetchCplSuggestion(d.id)}
                    title="Ø-CPL der letzten 90 Tage aus Meta Insights lesen und einen Lead-Wert vorschlagen"
                  >
                    {cplLoadingId === d.id ? "lade…" : `CPL-Vorschlag${d.label ? ` (${d.label})` : ""}`}
                  </button>
                  {r && (
                    <span className="text-text-secondary">
                      {r.ok ? (
                        <>
                          Ø-CPL {eur(r.cpl)} € → Vorschlag {eur(r.suggestion)} € (90 Tage, {r.leads} Leads)
                          <button
                            type="button"
                            className="ml-2 text-accent underline"
                            onClick={() => applyCplSuggestion(r.suggestion)}
                          >
                            übernehmen
                          </button>
                        </>
                      ) : (
                        <span className="text-text-tertiary">{cplErrorHint(r)}</span>
                      )}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border-primary text-left text-xs text-text-tertiary">
              <th className="py-1.5 pr-4 font-medium">Event-Typ</th>
              <th className="py-1.5 pr-4 font-medium">Anzahl</th>
              <th className="py-1.5 pr-4 font-medium text-accent">Meta</th>
              <th className="py-1.5 pr-4 font-medium" title="Serverseitiges Neukunden-Routing (aktiv): markierte Event-Typen feuern die Meta-Conversion nur für neue Kontakte (als LeadNewCustomer); bekannte Kontakte werden unterdrückt. Die Freigabe des Ursprungstyps (z. B. Lead) deckt das mit ab — LeadNewCustomer muss nicht zusätzlich angehakt werden.">Neukunden</th>
              <th className="py-1.5 pr-4 font-medium" title="Standard-Conversion-Wert (EUR) — wird an Browser-Pixel und CAPI gesendet, wenn das Event keinen eigenen Wert hat">Wert (€)</th>
              <th className="py-1.5 pr-4 font-medium text-text-tertiary/50">Google</th>
              <th className="py-1.5 font-medium text-text-tertiary/50">LinkedIn</th>
            </tr>
          </thead>
          <tbody>
            {displayItems.map((it) => {
              const isSynthetic = it.eventType === NEW_CUSTOMER_TYPE;
              return (
              <tr key={it.eventType} className="border-b border-border-secondary last:border-0">
                <td
                  className="py-1.5 pr-4 font-mono text-xs text-text-primary"
                  title={isSynthetic ? "Synthetisches Ziel-Event: neue Kontakte werden im Neukunden-Modus hierauf umgeroutet (→ Meta Lead)" : undefined}
                >
                  {it.eventType}
                  <HqMetaRowHint eventType={it.eventType} />
                  {it.connected && (
                    <span
                      className="ml-1 font-sans text-text-tertiary"
                      title="Standard-Event-Typ eines angebundenen Webhook-Endpunkts — noch kein Event empfangen"
                    >
                      · angebunden
                    </span>
                  )}
                </td>
                <td className="py-1.5 pr-4 text-xs text-text-tertiary">{isSynthetic ? "–" : it.count}</td>
                <td className="py-1.5 pr-4">
                  <input
                    type="checkbox"
                    checked={it.eventType === "PageView" || selected.includes(it.eventType)}
                    disabled={it.eventType === "PageView"}
                    onChange={() => toggleMeta(it.eventType)}
                    title={
                      it.eventType === "PageView"
                        ? "PageView wird immer serverseitig an Meta gesendet"
                        : undefined
                    }
                  />
                </td>
                <td className="py-1.5 pr-4">
                  <input
                    type="checkbox"
                    checked={selectedNewCustomer.includes(it.eventType)}
                    disabled={isSynthetic}
                    onChange={() => toggleNewCustomer(it.eventType)}
                    title={isSynthetic ? "LeadNewCustomer ist das Routing-Ziel, kein zählbarer Eingangstyp" : "Unter Neukunden-Modus zählen"}
                  />
                </td>
                <td className="py-1.5 pr-4">
                  {it.eventType !== "PageView" && (
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder={LEAD_VALUE_TYPES.includes(it.eventType) ? "10 (Auto)" : "–"}
                      className="w-20 rounded border border-border-primary bg-transparent px-1.5 py-0.5 text-xs"
                      value={eventValues[it.eventType]?.value ?? ""}
                      disabled={!goesToMeta(it.eventType)}
                      title={
                        goesToMeta(it.eventType)
                          ? "Standard-Wert in EUR — Lead-Events fallen ohne Eintrag auf 10 € zurück"
                          : "Erst Meta-Routing aktivieren"
                      }
                      onChange={(e) => setEventValue(it.eventType, e.target.value)}
                    />
                  )}
                </td>
                <td className="py-1.5 pr-4 opacity-50">
                  <input type="checkbox" disabled title="bald verfügbar" />
                </td>
                <td className="py-1.5 opacity-50">
                  <input type="checkbox" disabled title="bald verfügbar" />
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
        <OnlyHqNotice />
      </div>
    );

  const browserEvents = (
    <>
      {/* Fixed PageView default — informational, always on. */}
      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-border-secondary bg-bg-tertiary px-3 py-2 text-xs text-text-secondary">
        <span className="font-mono text-text-primary">PageView</span>
        <span>auf jeder Seite</span>
        <span className="ml-auto flex items-center gap-3">
          <label className="flex items-center gap-1 opacity-60"><input type="checkbox" checked disabled /> Browser</label>
          <label className="flex items-center gap-1 opacity-60"><input type="checkbox" checked disabled /> CAPI</label>
        </span>
        <span className="w-full text-[10px] text-text-tertiary">Standard, läuft immer mit (im Browser und über unseren Server).</span>
      </div>

      {rules.length > 0 && (
        <div className="space-y-2">
          {rules.map((r, i) => (
            <div
              key={i}
              className="flex flex-wrap items-center gap-2 rounded-md border border-border-secondary bg-bg-primary px-3 py-2"
            >
              <select
                className="rounded-md border border-border-primary bg-bg-secondary px-2 py-1.5 text-xs text-text-primary focus:border-accent focus:outline-none"
                value={r.match}
                onChange={(e) => updateRule(i, { match: e.target.value as EventRule["match"] })}
              >
                <option value="contains">URL enthält</option>
                <option value="exact">URL ist exakt</option>
              </select>
              <input
                className="w-48 rounded-md border border-border-primary bg-bg-secondary px-2 py-1.5 font-mono text-xs text-text-primary focus:border-accent focus:outline-none"
                placeholder="/danke-webinar"
                value={r.url}
                onChange={(e) => updateRule(i, { url: e.target.value })}
              />
              <span className="text-text-tertiary">→</span>
              <input
                className="w-36 rounded-md border border-border-primary bg-bg-secondary px-2 py-1.5 text-xs text-text-primary focus:border-accent focus:outline-none"
                placeholder="Lead"
                value={r.event_type}
                onChange={(e) => updateRule(i, { event_type: e.target.value })}
              />
              {(() => {
                // TASK-83: which Google targets this rule's event actually
                // reaches — server-side gate, mirrored here only for display
                // (dispatchCapi in the worker is the real authority). A rule's
                // own switch being off doesn't mean the event never sends —
                // the worker only suppresses when EVERY matching rule agrees
                // (matchedRules.every(r => r.google_ads === false) in
                // capi-dispatch.ts) — see googleToggleOffHint for the caveat.
                const stage = metaEventName(r.event_type);
                const gadsAction = gadsConversionActions[stage];
                const gadsOn = r.google_ads !== false;
                const ga4On = r.google_ga4 !== false;
                return (
                  <>
                    <RulePixelBox rule={r} goesToMeta={goesToMeta} onChange={(patch) => updateRule(i, patch)} />
                    <RulePixelConfirm rule={r} error={saveError} onChange={(patch) => updateRule(i, patch)} />
                    <RuleMetaHqOnly rule={r} onChange={(patch) => updateRule(i, patch)} />
                    {googleAdsDest && (
                      <label className="flex items-center gap-1 text-xs text-text-secondary">
                        <input
                          type="checkbox"
                          checked={gadsOn}
                          onChange={(e) => updateRule(i, { google_ads: e.target.checked })}
                        />{" "}
                        Auch an Google Ads senden
                      </label>
                    )}
                    {ga4Dest && (
                      <label className="flex items-center gap-1 text-xs text-text-secondary">
                        <input
                          type="checkbox"
                          checked={ga4On}
                          onChange={(e) => updateRule(i, { google_ga4: e.target.checked })}
                        />{" "}
                        Auch an GA4 senden
                      </label>
                    )}
                    <button
                      type="button"
                      onClick={() => removeRule(i)}
                      className="ml-auto text-text-tertiary hover:text-danger"
                      title="Regel entfernen"
                    >
                      <Trash2 size={14} />
                    </button>
                    <RuleMetaLine rule={r} goesToMeta={goesToMeta} />
                    {googleAdsDest &&
                      (!gadsOn ? (
                        <span className="w-full text-[10px] text-text-tertiary">
                          {googleToggleOffHint("Google Ads", "google_ads", r.event_type, rules)}
                        </span>
                      ) : gadsAction ? (
                        <span className="w-full text-[10px] text-success">
                          → Google Ads ({stage})
                        </span>
                      ) : (
                        <span className="w-full text-[10px] text-warning">
                          Google Ads: keine Conversion-Aktion für Stufe „{stage}“ hinterlegt.
                        </span>
                      ))}
                    {ga4Dest &&
                      (ga4On ? (
                        <span className="w-full text-[10px] text-success">→ GA4</span>
                      ) : (
                        <span className="w-full text-[10px] text-text-tertiary">
                          {googleToggleOffHint("GA4", "google_ga4", r.event_type, rules)}
                        </span>
                      ))}
                  </>
                );
              })()}
            </div>
          ))}
        </div>
      )}
      <button
        type="button"
        onClick={addRule}
        className="mt-2 inline-flex items-center gap-1 rounded-md border border-border-primary px-3 py-1.5 text-xs text-text-secondary hover:bg-bg-hover"
      >
        <Plus size={12} /> Regel hinzufügen
      </button>

      <div className="mt-4 rounded-md border border-border-primary bg-bg-secondary p-3">
        <p className="mb-1 text-xs font-medium text-text-primary">Für Entwickler: manuell aus dem Seitencode feuern</p>
        <div className="flex items-start gap-2">
          <code className="flex-1 overflow-x-auto whitespace-pre rounded-md bg-bg-tertiary px-3 py-2 font-mono text-[11px] text-text-primary">
            {DHC_TRACK_SNIPPET}
          </code>
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard.writeText(DHC_TRACK_SNIPPET);
              toast.success("Snippet kopiert");
            }}
            className="rounded-md border border-border-primary px-3 py-2 text-xs text-text-secondary hover:bg-bg-hover"
          >
            Kopieren
          </button>
        </div>
      </div>
    </>
  );

  const browserConversion = (
    <select
      className={`${inputCls} sm:max-w-xs`}
      value={form.browser_conversion_event ?? original.browser_conversion_event ?? ""}
      onChange={(e) => set("browser_conversion_event", e.target.value || null)}
    >
      <option value="">— aus (Default) —</option>
      <option value="CompleteRegistration">CompleteRegistration (Freebie-/Webinar-Leads)</option>
      <option value="Schedule">Schedule (Termin/Erstgespräch)</option>
      <option value="Lead">Lead (allgemein)</option>
      <option value="Purchase">Purchase (Kauf)</option>
      <option value="Contact">Contact (Kontaktformular)</option>
    </select>
  );

  const mergeBuffer = (
    <div className="flex flex-wrap items-center gap-2">
      <input
        type="number"
        min={0}
        max={600}
        className={`${inputCls} w-28`}
        placeholder="90"
        value={form.capi_merge_buffer_seconds ?? ""}
        onChange={(e) =>
          set(
            "capi_merge_buffer_seconds",
            e.target.value === "" ? null : Math.max(0, parseInt(e.target.value, 10) || 0),
          )
        }
      />
      <span className="text-xs text-text-tertiary">
        Sekunden — leer = Standard (90&nbsp;s), 0 = aus
      </span>
    </div>
  );

  return { eventsTable, browserEvents, browserConversion, mergeBuffer, save, saving: update.isPending };
}

/**
 * Data quality (split out of TrackingMechanicsSection): fingerprinting +
 * bot-filter. Self-contained config form (partial saves merge server-side, so
 * it never clobbers other tracking/consent settings).
 */
export function TrackingQualitySection({ projectId }: { projectId: number }) {
  const { data, isLoading } = useTrackingConfig(projectId);
  const update = useUpdateTrackingConfig(projectId);

  const [form, setForm] = useState<TrackingConfigInput>({});
  useEffect(() => {
    if (data?.config) {
      const c = data.config;
      setForm({
        fingerprint_mode: c.fingerprint_mode,
        bot_gate_enabled: c.bot_gate_enabled,
      });
    }
  }, [data?.config]);

  if (isLoading || !data) return <p className="text-sm text-text-secondary">Lade…</p>;
  const original = data.config;
  const set = <K extends keyof TrackingConfigInput>(k: K, v: TrackingConfigInput[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const fpMode = form.fingerprint_mode ?? original.fingerprint_mode;

  const save = () => {
    let ack = false;
    const fpEscalating =
      form.fingerprint_mode === "always" && original.fingerprint_mode !== "always";
    if (fpEscalating) {
      if (
        !confirm(
          "Geräte-Wiedererkennung OHNE Zustimmung: Jeder Besucher wird wiedererkannt, egal ob er zugestimmt hat. " +
            "Das ist erlaubnispflichtig (TDDDG §25) — nur mit dokumentierter Rechtsgrundlage nutzen. " +
            "Wird mit deinem Namen protokolliert. Fortfahren?",
        )
      ) {
        return;
      }
      ack = true;
    }
    update.mutate(
      { ...form, risk_acknowledged: ack },
      {
        onSuccess: () => toast.success("Datenqualität gespeichert"),
        onError: (e) => toast.error(`Speichern fehlgeschlagen: ${(e as Error).message}`),
      },
    );
  };

  return (
    <div className="space-y-6">
      <SettingsPanel>
        <SettingRow
          title="Fingerprinting (Geräte-Erkennung)"
          description="Erkennt ein Gerät auch ohne Cookies wieder, damit dieselbe Person über mehrere Besuche hinweg richtig zugeordnet wird."
        >
        <div className="space-y-2">
          {(
            [
              ["off", "Aus", "Geräte-Wiedererkennung ist aus."],
              ["consent", "Mit Consent (empfohlen)", "Läuft erst, wenn der Besucher der Marketing-Nutzung zugestimmt hat."],
              ["always", "Ohne Consent — für jeden Besucher", "⚠️ Läuft bei jedem Besucher, auch ohne Zustimmung. Das ist erlaubnispflichtig (TDDDG §25) — nur mit klarer Rechtsgrundlage nutzen. Wird protokolliert."],
            ] as [FingerprintMode, string, string][]
          ).map(([value, label, desc]) => (
            <label key={value} className="flex items-start gap-2 text-sm text-text-primary">
              <input
                type="radio"
                name="fp-mode-quality"
                className="mt-1"
                checked={fpMode === value}
                onChange={() => set("fingerprint_mode", value)}
              />
              <span>
                <span className="font-medium">{label}</span>
                <span className="block text-xs text-text-tertiary">{desc}</span>
              </span>
            </label>
          ))}
        </div>
        </SettingRow>

        <SettingRow
          title="Bot-Filter (Cloudflare Turnstile)"
          description="Sortiert automatische Zugriffe (Bots) aus, bevor wir etwas an Meta melden — so bekommt deine Werbung sauberere Daten und weniger Fake-Leads. Wird erst aktiv, wenn die nötigen Zugangsdaten hinterlegt sind."
        >
          <label className="flex items-center gap-2 text-sm text-text-primary">
            <input
              type="checkbox"
              checked={form.bot_gate_enabled ?? original.bot_gate_enabled}
              onChange={(e) => set("bot_gate_enabled", e.target.checked)}
            />
            Bot-Filter für dieses Projekt aktivieren
          </label>
        </SettingRow>
      </SettingsPanel>

      <button
        type="button"
        onClick={save}
        disabled={update.isPending}
        className="rounded-md border border-accent bg-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
      >
        Datenqualität speichern
      </button>
    </div>
  );
}

export function ProjectDestinationsTab() {
  const { projectId } = useTabContext();
  const { data, isLoading } = useDestinations(projectId);
  const create = useCreateDestination(projectId);
  const updateDest = useUpdateDestination(projectId);
  const del = useDeleteDestination(projectId);
  const test = useTestDestination(projectId);
  const emq = useDestinationEmq(projectId);
  const delivery = usePixelDelivery(projectId);
  const trackingConfig = useTrackingConfig(projectId);
  const metaPixels = useMetaPixels();
  const capiCheck = useMetaPixelCapiCheck();
  const mech = useTrackingMechanics(projectId);

  const [editing, setEditing] = useState<TrackingDestination | null>(null);
  // The multi-second website-conversions request must see a form opened while it runs.
  const editingRef = useRef(editing);
  editingRef.current = editing;
  const [form, setForm] = useState<TrackingDestinationInput | null>(null);
  // Load the MCC's client accounts only while the google_ads form is open.
  const gadsAccounts = useGoogleAdsAccounts(form?.type === "google_ads");
  const [pendingAck, setPendingAck] = useState<DeniedStage | null>(null);
  const [testingId, setTestingId] = useState<number | null>(null);
  const [emqResult, setEmqResult] = useState<{ destId: number; data: EmqResult } | null>(null);
  const [emqLoadingId, setEmqLoadingId] = useState<number | null>(null);
  const gadsWeb = useGoogleAdsWebConversions(projectId);
  const [gadsWebId, setGadsWebId] = useState<number | null>(null);

  // "Conversions zuordnen" panel: map funnel stages onto EXISTING account
  // conversions (alternative to the one-click website conversions).
  const gadsActions = useGoogleAdsConversionActions(projectId);
  const [gadsMapPanel, setGadsMapPanel] = useState<{
    destId: number;
    accountActions: GoogleAdsAccountAction[];
    mapping: Record<string, string>;
  } | null>(null);
  const GADS_STAGES = ["Lead", "CompleteRegistration", "Schedule", "Contact", "Purchase"];

  // Auto-load EMQ for active Meta destinations so the score is visible
  // without a click; the detail panel (match keys) stays behind the button.
  const [emqScores, setEmqScores] = useState<Record<number, EmqResult>>({});
  const emqFetchedFor = useRef<string>("");
  useEffect(() => {
    const metas = (data?.destinations ?? []).filter(
      (d) => d.type === "meta" && d.active && d.has_credentials,
    );
    const key = metas.map((d) => d.id).join(",");
    if (!key || key === emqFetchedFor.current) return;
    emqFetchedFor.current = key;
    for (const d of metas) {
      emq.mutate(d.id, {
        onSuccess: (r) => setEmqScores((m) => ({ ...m, [d.id]: r })),
        onError: () => {}, // Badge bleibt einfach weg — Details liefert der Button
      });
    }
  }, [data, emq]);

  const emqAvg = (r?: EmqResult): number | null => {
    const scores = (r?.events ?? [])
      .map((e) => e.composite_score)
      .filter((s): s is number => s != null);
    return scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : null;
  };
  const [pixelCheck, setPixelCheck] = useState<{ pixelId: string; needsToken: boolean } | null>(null);
  const checkTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Probe whether the system-user token can already send CAPI for a pixel
  // (green = no per-pixel token needed, warning + instruction if a token IS).
  const runCapiCheck = (pixelId: string) => {
    if (!/^\d{6,}$/.test(pixelId)) return;
    capiCheck.mutate(pixelId, {
      onSuccess: (r) => setPixelCheck({ pixelId, needsToken: r.needsToken }),
    });
  };
  // Dropdown pick → check immediately.
  const onPickPixel = (pixelId: string) => {
    setF("client_id", pixelId || null);
    setPixelCheck(null);
    if (pixelId) runCapiCheck(pixelId);
  };
  // Manual typing → check after a short debounce (also covers pasted ids).
  const onTypePixel = (pixelId: string) => {
    setF("client_id", pixelId);
    setPixelCheck(null);
    if (checkTimer.current) clearTimeout(checkTimer.current);
    checkTimer.current = setTimeout(() => runCapiCheck(pixelId.trim()), 600);
  };
  const copyTokenInstruction = (pixelId: string, pixelName: string) => {
    void navigator.clipboard.writeText(buildTokenInstruction(pixelId, pixelName));
    toast.success("Kunden-Anleitung kopiert");
  };

  if (isLoading || !data) return <p className="text-sm text-text-secondary">Lade…</p>;

  // Consent-dependency warning: a stage B/C destination in a consent category
  // only fires WITH consent. If no DHC banner collects it (mode off/externalCMP),
  // those destinations won't be sent server-side → warn the operator.
  const consentMode = trackingConfig.data?.config.consent_mode;
  const consentGap = consentMode === "off" || consentMode === "externalCMP";
  const affectedByConsentGap = consentGap
    ? data.destinations.filter(
        (d) =>
          d.active &&
          (d.category === "marketing" || d.category === "statistics") &&
          (d.denied_stage === "B" || d.denied_stage === "C"),
      )
    : [];

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm });
  };
  const openEdit = (d: TrackingDestination) => {
    setEditing(d);
    setForm({
      type: d.type,
      label: d.label,
      client_id: d.client_id,
      tag_snippet: d.tag_snippet,
      ad_account_ref: d.ad_account_ref,
      category: d.category,
      denied_stage: d.denied_stage,
      bypass_rules: d.bypass_rules,
      active: d.active,
      extra_config: d.extra_config ?? {},
      // server_credential intentionally omitted — write-only.
    });
  };
  const closeForm = () => {
    setForm(null);
    setEditing(null);
    setPendingAck(null);
  };

  const setF = <K extends keyof TrackingDestinationInput>(k: K, v: TrackingDestinationInput[K]) =>
    setForm((f) => (f ? { ...f, [k]: v } : f));
  const setExtra = (k: string, v: string) =>
    setForm((f) => (f ? { ...f, extra_config: { ...(f.extra_config ?? {}), [k]: v } } : f));
  const extraStr = (k: string): string => {
    const v = form?.extra_config?.[k];
    return typeof v === "string" ? v : "";
  };

  const doTest = (d: TrackingDestination) => {
    setTestingId(d.id);
    test.mutate(d.id, {
      onSuccess: (r) => {
        if (r.ok) {
          toast.success(
            `Meta hat das Test-Event empfangen${r.events_received ? ` (events_received: ${r.events_received})` : ""}. Prüfe Events Manager → Test-Events.`,
          );
        } else {
          toast.error(`Meta-Test fehlgeschlagen: ${r.error ?? "unbekannt"}`);
        }
      },
      onError: (e) => {
        const code = e instanceof ApiError ? ((e.body as { error?: string })?.error ?? e.message) : (e as Error).message;
        const msg =
          code === "test_event_code_required"
            ? "Bitte zuerst einen Test-Event-Code beim Ziel hinterlegen (Meta Events Manager → Test-Events)."
            : code === "missing_pixel_or_token"
              ? "Pixel-ID oder CAPI-Token fehlt."
              : `Test fehlgeschlagen: ${code}`;
        toast.error(msg);
      },
      onSettled: () => setTestingId(null),
    });
  };

  const submit = (ack = false) => {
    if (!form) return;
    const escalating =
      (form.denied_stage === "A" || form.denied_stage === "B") &&
      form.denied_stage !== (editing?.denied_stage ?? "C");
    if (escalating && !ack) {
      setPendingAck(form.denied_stage!);
      return;
    }
    const payload = { ...form, risk_acknowledged: ack };
    const onError = (e: unknown) =>
      toast.error(`Speichern fehlgeschlagen: ${(e as Error).message}`);
    if (editing) {
      updateDest.mutate(
        { destId: editing.id, input: payload },
        { onSuccess: () => { toast.success("Ziel aktualisiert"); closeForm(); }, onError },
      );
    } else {
      create.mutate(payload, {
        onSuccess: () => { toast.success("Ziel angelegt"); closeForm(); },
        onError,
      });
    }
  };

  const toggleActive = (d: TrackingDestination) =>
    updateDest.mutate(
      { destId: d.id, input: { active: !d.active } },
      {
        onError: (e) => {
          // 404 = the row in the UI no longer exists server-side (e.g. deleted
          // in another tab). The hook refetches the list on error, so the stale
          // row disappears — tell the user that plainly instead of a raw "404".
          const gone = e instanceof ApiError && e.status === 404;
          toast.error(
            gone
              ? "Dieses Tracking-Ziel existiert nicht mehr — die Liste wurde aktualisiert."
              : `Schalten fehlgeschlagen: ${(e as Error).message}`,
          );
        },
      },
    );

  const openGadsMapping = (d: TrackingDestination) => {
    if (gadsMapPanel?.destId === d.id) {
      setGadsMapPanel(null); // toggle off
      return;
    }
    gadsActions.mutate(d.id, {
      onSuccess: (r) => {
        if (!r.ok) {
          toast.error(`Konto-Conversions laden fehlgeschlagen: ${r.error ?? "unbekannt"}`);
          return;
        }
        setGadsMapPanel({
          destId: d.id,
          accountActions: r.actions,
          mapping: {
            ...((d.extra_config?.["conversion_actions"] as Record<string, string> | undefined) ?? {}),
          },
        });
      },
      onError: (e) => toast.error(`Konto-Conversions laden fehlgeschlagen: ${(e as Error).message}`),
    });
  };

  const saveGadsMapping = (d: TrackingDestination) => {
    if (!gadsMapPanel) return;
    const cleaned = Object.fromEntries(
      Object.entries(gadsMapPanel.mapping).filter(([, v]) => v),
    );
    updateDest.mutate(
      {
        destId: d.id,
        input: {
          extra_config: { ...(d.extra_config ?? {}), conversion_actions: cleaned },
        },
      },
      {
        onSuccess: () => {
          toast.success("Conversion-Zuordnung gespeichert");
          setGadsMapPanel(null);
        },
        onError: (e) => toast.error(`Speichern fehlgeschlagen: ${(e as Error).message}`),
      },
    );
  };

  const doGoogleAdsWebConversions = (d: TrackingDestination) => {
    setGadsWebId(d.id);
    gadsWeb.mutate(d.id, {
      onSuccess: (r) => {
        if (!r.ok) {
          toast.error(`Website-Conversions fehlgeschlagen: ${r.error ?? "unbekannt"}`);
          return;
        }
        // The button rewrote the mapping: an open edit form of this destination
        // still holds the old one — refill (or close) it. An open mapping panel
        // also lists the old account actions, so it is closed, not refilled.
        const fresh = r.fresh_extra_config;
        if (fresh) {
          setForm((f) =>
            f && editingRef.current?.id === d.id
              ? { ...f, extra_config: withFreshServerConfig(f.extra_config ?? {}, fresh) }
              : f,
          );
        } else if (editingRef.current?.id === d.id) {
          closeForm();
        }
        setGadsMapPanel((p) => (p && p.destId === d.id ? null : p));
        const done = Object.keys(r.web_conversions);
        if (r.missing.length > 0) {
          toast.warning(
            `Verknüpft: ${done.join(", ") || "keine"}. Fehlt: ${r.missing.join(", ")} — Google lieferte kein Snippet-Ziel, diese Stufen bleiben unverändert. Später erneut versuchen.`,
          );
        } else {
          toast.success(`Website-Conversions verknüpft: ${done.join(", ")}`);
        }
      },
      onError: (e) => toast.error(`Website-Conversions fehlgeschlagen: ${(e as Error).message}`),
      onSettled: () => setGadsWebId(null),
    });
  };

  const doEmq = (d: TrackingDestination) => {
    if (emqResult?.destId === d.id) {
      setEmqResult(null); // toggle off
      return;
    }
    // Auto-fetch already ran? Show its result without a second Graph call.
    const cached = emqScores[d.id];
    if (cached) {
      setEmqResult({ destId: d.id, data: cached });
      return;
    }
    setEmqLoadingId(d.id);
    emq.mutate(d.id, {
      onSuccess: (data) => setEmqResult({ destId: d.id, data }),
      onError: (e) => toast.error(`EMQ laden fehlgeschlagen: ${(e as Error).message}`),
      onSettled: () => setEmqLoadingId(null),
    });
  };

  const remove = (d: TrackingDestination) => {
    if (!confirm(`Ziel "${d.label ?? TYPE_LABEL[d.type]}" wirklich löschen?`)) return;
    del.mutate(d.id, {
      onSuccess: () => toast.success("Ziel gelöscht"),
      onError: (e) => toast.error(`Löschen fehlgeschlagen: ${(e as Error).message}`),
    });
  };

  return (
    <div className="space-y-6 pb-12">
      {!data.secretsConfigured && (
        <div className="flex items-center gap-2 rounded-md border border-warning bg-warning-bg px-4 py-3 text-sm text-text-primary">
          <AlertTriangle size={16} className="text-warning" />
          <span>
            Server-Credentials sind nicht konfiguriert (<code>DHC_SECRETS_KEY</code> fehlt).
            Ziele können angelegt werden, aber Tokens lassen sich nicht speichern.
          </span>
        </div>
      )}

      {affectedByConsentGap.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-warning bg-warning-bg px-4 py-3 text-sm text-text-primary">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-warning" />
          <div className="space-y-1">
            <p>
              <strong>Achtung — fehlende Einwilligung:</strong>{" "}
              {affectedByConsentGap.length === 1 ? "Ein Ziel" : `${affectedByConsentGap.length} Ziele`}{" "}
              ({affectedByConsentGap.map((d) => d.label || TYPE_LABEL[d.type]).join(", ")}){" "}
              {affectedByConsentGap.length === 1 ? "wird" : "werden"} nur mit Zustimmung gemeldet (Stufe{" "}
              {[...new Set(affectedByConsentGap.map((d) => d.denied_stage))].sort().join("/")}), aber es ist{" "}
              <strong>{consentMode === "off" ? "kein Banner aktiv" : "ein externes Cookie-Banner des Kunden im Einsatz"}</strong> —
              es läuft also kein eigenes Banner, das die Zustimmung einsammelt.
            </p>
            <p className="text-text-secondary">
              <ConsentGapAdvice consentMode={consentMode} />
            </p>
          </div>
        </div>
      )}

      <SettingsPanel>
      <SettingRow
        title="Tracking-Ziele"
        description="Hier legst du fest, an welche Werbe-Plattformen (z. B. Meta/Facebook) wir deine Ergebnisse melden — damit die Werbung lernt, wer sich bei dir einträgt oder kauft, und günstiger wird. Google & LinkedIn folgen."
        wide
      >
      <div className="space-y-4">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-1.5 rounded-md border border-accent bg-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
        >
          <Plus size={14} /> Ziel hinzufügen
        </button>
      </div>

      {data.destinations.length === 0 && (
        <p className="text-sm text-text-secondary">Noch keine Tracking-Ziele angelegt.</p>
      )}

      <div className="space-y-2">
        {data.destinations.map((d) => (
          <div
            key={d.id}
            className="flex flex-wrap items-center gap-3 rounded-lg border border-border-primary bg-bg-secondary p-4"
          >
            <span className="rounded bg-bg-tertiary px-2 py-0.5 text-xs font-medium text-text-primary">
              {TYPE_LABEL[d.type]}
            </span>
            <span className="text-sm text-text-primary">{d.label || d.client_id || "—"}</span>
            {d.type === "google_ads" &&
              Object.keys(
                (d.extra_config?.["conversion_actions"] as Record<string, string> | undefined) ?? {},
              ).map((stage) => (
                <span
                  key={stage}
                  className="rounded-full bg-success-bg px-2 py-0.5 text-[10px] font-medium text-success"
                  title="Conversion im Google-Ads-Konto verknüpft"
                >
                  {stage} ✓
                </span>
              ))}
            <span className={`rounded-full px-2 py-0.5 text-xs ${STAGE_BADGE[d.denied_stage]}`}>
              Stufe {d.denied_stage}
            </span>
            <span className="text-xs text-text-tertiary">{d.category}</span>
            {d.type === "google_ads" ? (
              <span className="text-xs text-text-tertiary" title="Autorisierung über das DHC-MCC (Env-Vars), kein eigener Token nötig">
                MCC-Auth
              </span>
            ) : d.has_credentials ? (
              <span className="text-xs text-success">Token ✓</span>
            ) : (
              <span className="text-xs text-text-tertiary">kein Token</span>
            )}
            <div className="ml-auto flex items-center gap-2">
              <label className="flex items-center gap-1 text-xs text-text-secondary">
                <input type="checkbox" checked={d.active} onChange={() => toggleActive(d)} />
                aktiv
              </label>
              {d.type === "google_ads" && (
                <>
                  <WebConversionButton
                    pending={gadsWebId === d.id}
                    onClick={() => doGoogleAdsWebConversions(d)}
                  />
                  <button
                    type="button"
                    onClick={() => openGadsMapping(d)}
                    disabled={gadsActions.isPending}
                    className="inline-flex items-center gap-1 rounded-md border border-border-primary px-2 py-1 text-xs text-text-secondary hover:bg-bg-hover disabled:opacity-50"
                    title="Bestehende Conversions aus dem Google-Ads-Konto mit unseren Event-Stufen verbinden"
                  >
                    {gadsActions.isPending
                      ? "lade…"
                      : gadsMapPanel?.destId === d.id
                        ? "Zuordnen ▲"
                        : "Conversions zuordnen"}
                  </button>
                </>
              )}
              {d.type === "meta" && d.has_credentials && (
                <>
                  {(() => {
                    const avg = emqAvg(emqScores[d.id]);
                    return avg != null ? (
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                          avg >= 7
                            ? "bg-success-bg text-success"
                            : avg >= 5
                              ? "bg-warning-bg text-warning"
                              : "bg-danger-bg text-danger"
                        }`}
                        title="Ø Event Match Quality (Meta) über alle Events — Details über den EMQ-Button"
                      >
                        EMQ ∅ {avg.toFixed(1)}/10
                      </span>
                    ) : null;
                  })()}
                  <button
                    type="button"
                    onClick={() => doEmq(d)}
                    disabled={emqLoadingId === d.id}
                    className="inline-flex items-center gap-1 rounded-md border border-border-primary px-2 py-1 text-xs text-text-secondary hover:bg-bg-hover disabled:opacity-50"
                    title="Event Match Quality von Meta abrufen (Datenqualität deines Pixels)"
                  >
                    {emqLoadingId === d.id ? "lade…" : emqResult?.destId === d.id ? "EMQ ▲" : "EMQ"}
                  </button>
                  <button
                    type="button"
                    onClick={() => doTest(d)}
                    disabled={testingId === d.id}
                    className="inline-flex items-center gap-1 rounded-md border border-border-primary px-2 py-1 text-xs text-text-secondary hover:bg-bg-hover disabled:opacity-50"
                    title="Meta-Test-Event senden (erscheint in Events Manager → Test-Events)"
                  >
                    <Send size={12} /> {testingId === d.id ? "sende…" : "Test"}
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => openEdit(d)}
                className="rounded-md border border-border-primary p-1.5 text-text-secondary hover:bg-bg-hover"
                aria-label="Bearbeiten"
              >
                <Pencil size={14} />
              </button>
              <button
                type="button"
                onClick={() => remove(d)}
                className="rounded-md border border-border-primary p-1.5 text-danger hover:bg-danger-bg"
                aria-label="Löschen"
              >
                <Trash2 size={14} />
              </button>
            </div>

            {d.type === "google_ads" && <WebConversionNotice extraConfig={d.extra_config} />}

            {gadsMapPanel?.destId === d.id && (
              <div className="w-full space-y-2 border-t border-border-primary pt-2">
                <p className="text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
                  Event-Stufe → Google-Ads-Conversion
                </p>
                {gadsMapPanel.accountActions.length === 0 && (
                  <p className="text-xs text-text-tertiary">
                    Keine aktiven Conversions im Konto gefunden — „Website-Conversions anlegen" nutzen.
                  </p>
                )}
                {GADS_STAGES.map((stage) => (
                  <div key={stage} className="flex items-center gap-2 text-xs">
                    <span className="w-44 font-mono text-text-primary">{stage}</span>
                    <select
                      className="min-w-64 rounded border border-border-primary bg-transparent px-2 py-1 text-xs text-text-primary"
                      value={gadsMapPanel.mapping[stage] ?? ""}
                      onChange={(e) =>
                        setGadsMapPanel((pnl) =>
                          pnl
                            ? { ...pnl, mapping: { ...pnl.mapping, [stage]: e.target.value } }
                            : pnl,
                        )
                      }
                    >
                      <option value="">— nicht an Google senden —</option>
                      {gadsMapPanel.accountActions.map((a) => (
                        <option key={a.resourceName} value={a.resourceName}>
                          {a.name}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => saveGadsMapping(d)}
                    disabled={updateDest.isPending}
                    className="rounded-md bg-accent px-3 py-1 text-xs font-medium text-black hover:opacity-90 disabled:opacity-50"
                  >
                    {updateDest.isPending ? "speichere…" : "Zuordnung speichern"}
                  </button>
                  <span className="text-[10px] text-text-tertiary">
                    PageView wird nie an Google Ads übertragen (uploadClickConversions ist nur für
                    echte Conversions; Seitenaufrufe laufen bei Google über Tag/GA4).
                  </span>
                </div>
              </div>
            )}
            {emqResult?.destId === d.id && (
              <div className="w-full border-t border-border-primary pt-2">
                {!emqResult.data.ok ? (
                  <p className="text-xs text-danger">
                    EMQ nicht verfügbar: {emqResult.data.error}. (Token braucht Dataset-Zugriff; Werte
                    erscheinen erst nach ein paar gesendeten Events.)
                  </p>
                ) : (emqResult.data.events ?? []).length === 0 ? (
                  <p className="text-xs text-text-tertiary">
                    Noch keine EMQ-Daten — Meta zeigt Werte erst nach einigen gematchten Events.
                  </p>
                ) : (
                  <div className="space-y-2">
                    <p className="text-[10px] font-mono uppercase tracking-wider text-text-tertiary">
                      Event Match Quality (Meta)
                    </p>
                    {emqResult.data.events!.map((ev) => (
                      <div key={ev.event_name} className="text-xs">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-text-primary">{ev.event_name}</span>
                          {ev.composite_score != null && (
                            <span
                              className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                                ev.composite_score >= 7
                                  ? "bg-success-bg text-success"
                                  : ev.composite_score >= 5
                                    ? "bg-warning-bg text-warning"
                                    : "bg-danger-bg text-danger"
                              }`}
                            >
                              {ev.composite_score.toFixed(1)}/10
                            </span>
                          )}
                        </div>
                        {ev.match_keys.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-text-tertiary">
                            {ev.match_keys.map((k) => (
                              <span key={k.identifier}>
                                {k.identifier}: <strong className="text-text-secondary">{k.coverage}%</strong>
                              </span>
                            ))}
                          </div>
                        )}
                        {(ev.diagnostics ?? []).length > 0 && (
                          <ul className="mt-1 space-y-0.5 text-[10px] text-warning">
                            {ev.diagnostics!.map((diag) => (
                              <li key={diag.name} title={diag.description}>
                                ⚠ {diag.name}
                                {diag.percentage != null ? ` (${diag.percentage.toFixed(0)}% betroffen)` : ""}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                    <p className="text-[10px] text-text-tertiary">
                      Mehr übermittelte Merkmale = bessere Zuordnung deiner Conversions. Ziel: 7+/10.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {delivery.data && (delivery.data.total_fired > 0 || delivery.data.total_blocked > 0) && (
        <div className="rounded-lg border border-border-primary bg-bg-secondary px-4 py-3">
          <h3 className="font-mono text-xs uppercase tracking-wider text-text-tertiary">
            Pixel-Auslieferung (30 Tage)
          </h3>
          <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
            <span className="text-text-primary">
              Auslieferungsrate:{" "}
              <strong className={delivery.data.delivery_rate >= 0.9 ? "text-success" : delivery.data.delivery_rate >= 0.7 ? "text-warning" : "text-danger"}>
                {(delivery.data.delivery_rate * 100).toFixed(1)}%
              </strong>
            </span>
            <span className="text-text-secondary">gefeuert: {delivery.data.total_fired}</span>
            <span className="text-text-secondary">blockiert: {delivery.data.total_blocked}</span>
          </div>
          <p className="mt-1 text-xs text-text-tertiary">
            Anteil der Browser-Pixel, die wirklich geladen haben (der Rest wird von Werbeblockern verhindert). Die Meldung direkt von unserem Server funktioniert davon unabhängig.
          </p>
        </div>
      )}


      {form && (
        <div className="space-y-4 rounded-lg border border-accent bg-bg-primary p-5">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-text-primary">
              {editing ? "Ziel bearbeiten" : "Neues Tracking-Ziel"}
            </h3>
            <button type="button" onClick={closeForm} aria-label="Schließen">
              <X size={16} className="text-text-tertiary hover:text-text-primary" />
            </button>
          </div>

          <div className="flex items-center gap-3 rounded-md border border-border-primary bg-bg-secondary px-3 py-2 text-xs text-text-secondary">
            <span>Plattform:</span>
            <select
              className="rounded border border-border-primary bg-transparent px-2 py-1 text-xs text-text-primary"
              value={form.type ?? "meta"}
              disabled={!!editing}
              onChange={(e) => setF("type", e.target.value as DestinationType)}
            >
              <option value="meta">Meta (Conversions API)</option>
              <option value="google_ads">Google Ads (Offline Conversions)</option>
            </select>
            <span className="text-text-tertiary">
              {form.type === "google_ads"
                ? "Voraussetzung: Kundenkonto ist unter dem DHC-MCC verknüpft."
                : "LinkedIn folgt in einem späteren Schritt."}
            </span>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs text-text-tertiary">Bezeichnung</label>
              <input
                className={inputCls}
                placeholder={
                  form.type === "google_ads"
                    ? "z.B. Google Ads Kunde XY"
                    : "z.B. Meta Pixel Hauptfunnel"
                }
                value={form.label ?? ""}
                onChange={(e) => setF("label", e.target.value)}
              />
            </div>
            {form.type === "google_ads" && (
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs text-text-tertiary">
                  Google Ads Konto (unter dem DHC-MCC)
                </label>
                {(gadsAccounts.data?.accounts.length ?? 0) > 0 ? (
                  <Combobox
                    value={
                      gadsAccounts.data?.accounts.some((a) => a.id === form.client_id)
                        ? (form.client_id ?? "")
                        : ""
                    }
                    options={gadsAccounts.data!.accounts.map((a) => ({
                      value: a.id,
                      label: `${a.name} (${a.id})`,
                    }))}
                    onChange={(id) => setF("client_id", id || null)}
                    placeholder="Konto-Name oder Customer-ID eingeben…"
                  />
                ) : (
                  <input
                    className={inputCls}
                    placeholder={
                      gadsAccounts.isLoading
                        ? "Lade Konten aus dem MCC…"
                        : "z.B. 123-456-7890 (Kontoliste nicht verfügbar — ID manuell eintragen)"
                    }
                    value={form.client_id ?? ""}
                    onChange={(e) => setF("client_id", e.target.value)}
                  />
                )}
                {gadsAccounts.data && !gadsAccounts.data.ok && (
                  <p className="mt-1 text-[11px] text-warning">
                    Kontoliste nicht ladbar: {gadsAccounts.data.error ?? "unbekannt"} — Customer-ID manuell eintragen.
                  </p>
                )}
                <p className="mt-1 text-[11px] text-text-tertiary">
                  Nach dem Anlegen: „Website-Conversions anlegen" (erstellt DHC Lead,
                  DHC Contact, DHC Purchase als Website-Conversions — einmalig, kampagnenübergreifend) oder über
                  „Conversions zuordnen" bestehende Konto-Conversions verbinden.
                </p>
              </div>
            )}
            {form.type !== "google_ads" && (metaPixels.data?.pixels.length ?? 0) > 0 && (
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs text-text-tertiary">
                  Pixel auswählen (suchen/tippen)
                </label>
                <Combobox
                  value={
                    metaPixels.data?.pixels.some((p) => p.id === form.client_id)
                      ? (form.client_id ?? "")
                      : ""
                  }
                  options={metaPixels.data!.pixels.map((p) => ({
                    value: p.id,
                    label: `${p.name} (${p.id})`,
                  }))}
                  onChange={onPickPixel}
                  placeholder="Pixel-Name oder ID eingeben…"
                />
              </div>
            )}
            {form.type !== "google_ads" && (
              <div>
                <label className="mb-1 block text-xs text-text-tertiary">Pixel-ID (manuell)</label>
                <input
                  className={inputCls}
                  placeholder="z.B. 1234567890123456"
                  value={form.client_id ?? ""}
                  onChange={(e) => onTypePixel(e.target.value)}
                />
              </div>
            )}
            {/* CAPI-access feedback — shows for both dropdown pick and manual entry. */}
            {(capiCheck.isPending ||
              (pixelCheck && pixelCheck.pixelId === form.client_id)) && (
              <div className="sm:col-span-2">
                {capiCheck.isPending ? (
                  <p className="text-[11px] text-text-tertiary">Prüfe CAPI-Zugriff für dieses Pixel…</p>
                ) : pixelCheck && !pixelCheck.needsToken ? (
                  <p className="flex items-center gap-1 text-[11px] text-success">
                    <Check size={12} /> CAPI läuft über den System-User — kein eigener Token nötig.
                  </p>
                ) : (
                  <div className="flex flex-wrap items-center gap-2 rounded-md border border-warning bg-warning-bg px-3 py-2">
                    <AlertTriangle size={14} className="shrink-0 text-warning" />
                    <span className="text-[11px] text-text-primary">
                      Kein CAPI-Zugriff über den System-User — für dieses Pixel ist ein eigener CAPI-Token nötig.
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        copyTokenInstruction(
                          form.client_id!,
                          metaPixels.data?.pixels.find((p) => p.id === form.client_id)?.name ?? "",
                        )
                      }
                      className="ml-auto rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-[11px] text-text-secondary hover:bg-bg-hover"
                    >
                      Kunden-Anleitung kopieren
                    </button>
                  </div>
                )}
              </div>
            )}
            {form.type !== "google_ads" && (
            <div>
              <label className="mb-1 block text-xs text-text-tertiary">
                CAPI-Token{" "}
                {pixelCheck && pixelCheck.pixelId === form.client_id && !pixelCheck.needsToken
                  ? "(nicht nötig — System-User)"
                  : "(optional bei System-User-Pixel)"}{" "}
                {editing?.has_credentials && "(gespeichert — leer lassen zum Behalten)"}
              </label>
              <input
                type="password"
                className={inputCls}
                disabled={!!(pixelCheck && pixelCheck.pixelId === form.client_id && !pixelCheck.needsToken)}
                placeholder={editing?.has_credentials ? "•••••••• gespeichert" : "leer = System-User-Token"}
                value={form.server_credential ?? ""}
                onChange={(e) => setF("server_credential", e.target.value)}
              />
            </div>
            )}
            {form.type !== "google_ads" && (
            <div>
              <label className="mb-1 block text-xs text-text-tertiary">
                Test-Event-Code (optional, für Metas Test-Events-Tool)
              </label>
              <input
                className={inputCls}
                placeholder="z.B. TEST12345"
                value={extraStr("test_event_code")}
                onChange={(e) => setExtra("test_event_code", e.target.value)}
              />
            </div>
            )}
            <div>
              <label className="mb-1 block text-xs text-text-tertiary">Verhalten bei Ablehnung</label>
              <select
                className={inputCls}
                value={form.denied_stage}
                onChange={(e) => setF("denied_stage", e.target.value as DeniedStage)}
              >
                <option value="C">{STAGE_LABEL.C}</option>
                <option value="B">{STAGE_LABEL.B}</option>
                <option value="A">{STAGE_LABEL.A}</option>
              </select>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-text-primary">
            <input
              type="checkbox"
              checked={form.active ?? true}
              onChange={(e) => setF("active", e.target.checked)}
            />
            Aktiv
          </label>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => submit(false)}
              disabled={create.isPending || updateDest.isPending}
              className="rounded-md border border-accent bg-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {editing ? "Speichern" : "Anlegen"}
            </button>
            <button
              type="button"
              onClick={closeForm}
              className="rounded-md border border-border-primary px-4 py-2 text-sm text-text-secondary hover:bg-bg-hover"
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}

      </div>
      </SettingRow>

      <SettingRow
        title="Welche Events an Meta?"
        description="Welche Ereignisse an Meta gemeldet werden. Seitenaufrufe gehen immer mit; hak zusätzlich nur die wirklich wichtigen an (z. B. „Lead“), damit Meta saubere Daten bekommt."
        wide
      >
        {mech.eventsTable}
      </SettingRow>
      </SettingsPanel>

      <Disclosure title="Erweitert: Browser-Events & Entwickler">
        <SettingsPanel>
          <SettingRow
            title="Browser-Events nach URL (No-Code)"
            description="Lege fest: Wenn die Adresse einer Seite passt, wird ein bestimmtes Ereignis gemeldet — im Browser und/oder direkt von unserem Server. Meta zählt es trotzdem nur einmal, auch wenn beide Wege aktiv sind."
            wide
          >
            {mech.browserEvents}
          </SettingRow>
          <SettingRow
            title="Browser-Conversion (optional, Meta-Dedup)"
            description="Conversions melden wir normalerweise direkt von unserem Server. Optional kann zusätzlich EIN Ereignis auch im Browser an Meta gehen (nur mit Marketing-Zustimmung). Das verbessert die Zuordnung, ersetzt den Server-Weg aber nicht."
            wide
          >
            {mech.browserConversion}
          </SettingRow>
          <SettingRow
            title="CAPI-Merge-Fenster"
            description="Kommt dieselbe Conversion über zwei Wege rein (Danke-Seite im Browser UND Mailtool-Webhook), warten wir kurz und melden sie nur EINMAL an Meta — mit den besten Daten aus beiden. Der Browser-Pixel feuert davon unabhängig sofort. Höher = mehr Doppel-Treffer werden zusammengeführt, aber etwas mehr Verzögerung beim Server-Versand."
          >
            {mech.mergeBuffer}
          </SettingRow>
        </SettingsPanel>
      </Disclosure>

      {/* One save button for the whole shared tracking form (Meta-routing table
          + advanced browser events + merge buffer). Lives OUTSIDE the "Erweitert"
          disclosure so toggling a Meta checkbox above always has a visible save. */}
      <button
        type="button"
        onClick={mech.save}
        disabled={mech.saving}
        className="rounded-md border border-accent bg-accent px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
      >
        {mech.saving ? "Speichere…" : "Tracking-Einstellungen speichern"}
      </button>

      {pendingAck && (
        <RiskAckDialog
          stage={pendingAck}
          onConfirm={() => submit(true)}
          onCancel={() => setPendingAck(null)}
        />
      )}
    </div>
  );
}
